import React, { useCallback, useEffect, useMemo, useState } from 'react'

import { useParams } from 'react-router-dom'
import useSWR, { mutate } from 'swr'

import { getCoreRowModel, useReactTable } from '@tanstack/react-table'
import { arrayMove } from '@dnd-kit/sortable'

import { create } from 'zustand'
import { getColumns } from './data-table-columns'
import DataTableToolbar from './data-table-toolbar'

import { useUI } from 'Editor'
import SortableDataTable from './sortable-data-table'
import { putFetcher } from 'api/swr'
import { toast } from '@/components/ui/use-toast'
import { displayedPickerCards } from 'Editor/lib/visibleComponents'

export const useForm = create((set) => ({
  data: [],
  setData: (data) => set({ data }),
}))

const layerZ = (layer) => Number(layer?.z ?? 0)

const sortLayerRows = (list, sortBy) => {
  const draft = []
  const rest = []
  for (const item of list) {
    if (item.id === 'draft') draft.push(item)
    else rest.push(item)
  }
  if (sortBy === 'z') {
    rest.sort(
      (a, b) => layerZ(a) - layerZ(b) || (a.playIndex ?? 0) - (b.playIndex ?? 0),
    )
  } else {
    rest.sort((a, b) => (a.playIndex ?? 0) - (b.playIndex ?? 0))
  }
  return [...draft, ...rest]
}

/** 叠层顺序拖拽：插到相邻两层之间，不把整表 z 压成 0..n */
const zForInsert = (prevZ, nextZ) => {
  if (prevZ == null && nextZ == null) return 0
  if (prevZ == null) return nextZ - 1
  if (nextZ == null) return prevZ + 1
  if (nextZ - prevZ > 1) return Math.floor((prevZ + nextZ) / 2)
  return prevZ
}

const withDraftFirst = (draftRow, rows) =>
  draftRow ? [draftRow, ...rows] : rows

const Layers = () => {
  const { tid } = useParams()

  const { data: layers } = useSWR(['/layers', { tid }])
  const { data: cpts } = useSWR(['/components', { tid }])
  const { data: dlcs } = useSWR(['/dlcs', { tid }])
  const { data: layerGroups } = useSWR(['/layer-groups'])

  const groupNameById = useMemo(() => {
    const map = new Map()
    layerGroups
      ?.filter((group) => group.state !== -1)
      .forEach((group) => map.set(group.id, group.name))
    return map
  }, [layerGroups])
  const lid = useUI((state) => state.lid)
  const toggleLayer = useUI((state) => state.toggleLayer)

  const { setData } = useForm((state) => state)

  const [rowSelection, setRowSelection] = useState({ [lid]: true })
  const [bulkIds, setBulkIds] = useState(() => new Set())
  const [localData, setLocalData] = useState(null)
  const [sortBy, setSortBy] = useState('playIndex')
  const [zDisplayReversed, setZDisplayReversed] = useState(false)

  const handleSortByChange = (value) => {
    setLocalData(null)
    setSortBy(value)
  }

  const data = useMemo(() => {
    const list = sortLayerRows(
      layers?.map((layer) => ({
        ...layer,
        cptCount: displayedPickerCards(cpts, tid, dlcs, layer.id).length,
        groupName: layer.gid ? groupNameById.get(layer.gid) : '',
      })) ?? [],
      sortBy,
    )
    setData(list)
    return list
  }, [layers, cpts, dlcs, tid, groupNameById, setData, sortBy])

  const realData = localData ?? data
  const displayData = useMemo(() => {
    if (sortBy !== 'z' || !zDisplayReversed) return realData
    const draftRow = realData.find((item) => item.id === 'draft')
    const rest = realData.filter((item) => item.id !== 'draft').slice().reverse()
    return withDraftFirst(draftRow, rest)
  }, [realData, sortBy, zDisplayReversed])

  useEffect(() => {
    if (lid) {
      setRowSelection({ [lid]: true })
    }
  }, [lid])

  useEffect(() => {
    const firstKey = Object.keys(rowSelection)[0]
    if (firstKey) {
      toggleLayer(firstKey)
    }
  }, [rowSelection, toggleLayer])

  const handleDragEnd = useCallback(
    async (event) => {
      const { active, over } = event
      if (!over || active.id === over.id) return

      const draggableItems = realData.filter((item) => item.id !== 'draft')
      const oldIndex = draggableItems.findIndex((item) => item.id === active.id)
      const newIndex = draggableItems.findIndex((item) => item.id === over.id)
      if (oldIndex === -1 || newIndex === -1) return

      const draftRow = realData.find((item) => item.id === 'draft')
      const movedRows = arrayMove(draggableItems, oldIndex, newIndex)

      if (sortBy === 'z') {
        const moved = movedRows[newIndex]
        const prev = movedRows[newIndex - 1]
        const next = movedRows[newIndex + 1]
        const nextZ = zForInsert(
          prev ? layerZ(prev) : null,
          next ? layerZ(next) : null,
        )
        const reordered = movedRows.map((item, index) =>
          index === newIndex ? { ...item, z: nextZ } : item,
        )
        setLocalData(withDraftFirst(draftRow, reordered))
        if (nextZ === layerZ(moved)) return
        try {
          await putFetcher(`/layers/${moved.id}`, {
            arg: { tid, key: 'z', value: nextZ },
          })
          await mutate(['/layers', { tid }])
          setLocalData(null)
        } catch {
          toast({
            variant: 'destructive',
            title: 'Z index update failed',
            description:
              'Could not save the new layer stacking. Please try again.',
          })
          setLocalData(null)
        }
        return
      }

      const reordered = movedRows.map((item, index) => ({
        ...item,
        playIndex: index,
      }))
      setLocalData(withDraftFirst(draftRow, reordered))

      const orderChanged = reordered.some((item, index) => {
        const original = draggableItems[index]
        return original?.id !== item.id
      })

      if (!orderChanged) {
        setLocalData(null)
        return
      }

      try {
        await putFetcher('/layers/reorder', {
          arg: { tid, ids: reordered.map((item) => item.id) },
        })
        await mutate(['/layers', { tid }])
        setLocalData(null)
      } catch {
        toast({
          variant: 'destructive',
          title: 'Order update failed',
          description: 'Could not save the new layer order. Please try again.',
        })
        setLocalData(null)
      }
    },
    [tid, realData, sortBy],
  )

  const toggleBulkId = useCallback((id) => {
    setBulkIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }, [])

  const selectableIds = useMemo(
    () => displayData.filter((item) => item.id && item.id !== 'draft').map((item) => item.id),
    [displayData],
  )
  const selectedLayerRows = useMemo(
    () => displayData.filter((item) => bulkIds.has(item.id)),
    [displayData, bulkIds],
  )
  const allSelected =
    selectableIds.length > 0 && selectableIds.every((id) => bulkIds.has(id))
  const someSelected = selectableIds.some((id) => bulkIds.has(id)) && !allSelected

  const columns = useMemo(
    () =>
      getColumns({
        selectedIds: bulkIds,
        onToggleSelect: toggleBulkId,
        allSelected,
        someSelected,
        onToggleAll: (checked) => {
          setBulkIds(checked ? new Set(selectableIds) : new Set())
        },
      }),
    [bulkIds, toggleBulkId, allSelected, someSelected, selectableIds],
  )

  const table = useReactTable({
    data: displayData,
    columns,
    state: {
      rowSelection,
    },
    getRowId: (row) => row.id,
    enableRowSelection: true,
    enableMultiRowSelection: false,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
  })

  return (
    <div className="flex h-full min-h-0 w-full flex-col gap-3">
      <div className="shrink-0">
        <DataTableToolbar
          layers={layers}
          sortBy={sortBy}
          onSortByChange={handleSortByChange}
          zDisplayReversed={zDisplayReversed}
          onToggleZDisplay={() => setZDisplayReversed((prev) => !prev)}
          selectedIds={bulkIds}
          selectedLayers={selectedLayerRows}
          onClearSelection={() => setBulkIds(new Set())}
        />
      </div>
      <div className="min-h-0 flex-1 overflow-hidden rounded-md border">
        <div className="h-full overflow-y-auto">
          <SortableDataTable table={table} onDragEnd={handleDragEnd} />
        </div>
      </div>
    </div>
  )
}

export default Layers
